import React, { useEffect } from "react";
import {
  ArrowRight,
  ShoppingBag,
  Heart,
  Activity,
  DollarSign,
  BookOpen,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Category } from "@/lib/supabase";

interface CategoryProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  path: string;
  delay: number;
}

interface FeaturedCategoriesProps {
  categories: Category[];
  isLoading: boolean;
}

const CategoryCard = ({
  icon,
  title,
  description,
  path,
  delay,
}: CategoryProps) => (
  <div
    className="rounded-xl overflow-hidden hover-lift glass-card animate-fade-in opacity-0 transition-all duration-300 hover:shadow-xl border-2 border-[#1A2B4D] hover:border-gold"
    style={{ animationDelay: `${delay}s` }}
  >
    <div className="p-6 md:p-6 flex flex-col h-full">
      <div className="w-16 h-16 mb-4 rounded-full bg-[#1A2B4D]/10 flex items-center justify-center text-gold">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-[#1A2B4D] mb-3">{title}</h3>
      <p className="text-[#1A2B4D]/70 mb-6 flex-grow line-clamp-3">
        {description}
      </p>
      <Link to={path}>
        <div className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-[#1A2B4D] hover:bg-gold text-white hover:text-[#1A2B4D] font-medium transition-all duration-300 group border border-transparent">
          <span>Explore</span>
          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
        </div>
      </Link>
    </div>
  </div>
);

const FeaturedCategories = ({
  categories,
  isLoading,
}: FeaturedCategoriesProps) => {
  // Map các icon dựa vào tên danh mục
  const getCategoryIcon = (slug: string) => {
    // console.log("Mapping icon cho slug:", slug);
    switch (slug) {
      case "smart-living":
        return <DollarSign className="h-8 w-8" />;
      case "beauty":
        return <Heart className="h-8 w-8" />;
      case "health":
        return <Activity className="h-8 w-8" />;
      case "shop":
        return <ShoppingBag className="h-8 w-8" />;
      case "blog":
        return <BookOpen className="h-8 w-8" />;
      default:
        // console.log("Không tìm thấy icon tương ứng cho slug:", slug);
        return <DollarSign className="h-8 w-8" />;
    }
  };

  // Fallback data chỉ được sử dụng khi không có dữ liệu từ API
  // const defaultCategories = [
  //   {
  //     icon: <DollarSign className="h-6 w-6" />,
  //     title: "Smart Living",
  //     description:
  //       "Elevate your everyday life with shopping tips, finance guides, and home organization strategies.",
  //     path: "/category/smart-living",
  //     delay: 0.1,
  //   },
  //   {
  //     icon: <Heart className="h-6 w-6" />,
  //     title: "Glow & Beauty",
  //     description:
  //       "Unlock your radiance with expert skincare routines, makeup trends, and fashion guidance.",
  //     path: "/category/beauty",
  //     delay: 0.2,
  //   },
  //   {
  //     icon: <Activity className="h-6 w-6" />,
  //     title: "Health & Family",
  //     description:
  //       "Balance, nurture & thrive with nutrition knowledge, exercise routines, and family wellness tips.",
  //     path: "/category/health",
  //     delay: 0.3,
  //   },
  //   {
  //     icon: <ShoppingBag className="h-6 w-6" />,
  //     title: "Shop & Reviews",
  //     description:
  //       "Shop smart, live chic with product reviews, deal alerts, and exclusive discounts from top brands.",
  //     path: "/category/shop",
  //     delay: 0.4,
  //   },
  // ];

  // Descriptions cho các danh mục (có thể bổ sung trong database sau)
  const descriptions: Record<string, string> = {
    "smart-living":
      "Elevate your everyday life with shopping tips, finance guides, and home organization strategies.",
    beauty:
      "Unlock your radiance with expert skincare routines, makeup trends, and fashion guidance.",
    health:
      "Balance, nurture & thrive with nutrition knowledge, exercise routines, and family wellness tips.",
    shop: "Shop smart, live chic with product reviews, deal alerts, and exclusive discounts from top brands.",
    blog: "Get inspired with our stories, trending topics and personal growth articles from our community.",
  };

  // Sử dụng dữ liệu từ API nếu có, nếu không sẽ dùng dữ liệu mặc định
  const displayCategories = categories.reverse().map((category, index) => {
    // console.log("Đang xử lý category:", category);
    return {
      icon: getCategoryIcon(category.slug),
      title: category.name,
      description:
        descriptions[category.slug] ||
        `Explore our ${category.name} articles and resources.`,
      path: `/articles?category=${category.slug}`,
      delay: 0.1 * (index + 1),
    };
  });

  // console.log("displayCategories cuối cùng:", displayCategories);

  return (
    <section id="featured-categories" className="py-16 md:py-24 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6 w-full max-w-full">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A2B4D]">
            Featured Categories
          </h2>
          <p className="mt-4 text-lg text-[#1A2B4D]/70 max-w-2xl mx-auto">
            Explore our curated content designed to inspire and enhance every
            aspect of your life
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
            {[1, 2, 3, 4, 5].map((_, index) => (
              <div
                key={index}
                className="rounded-xl overflow-hidden glass-card"
              >
                <div className="p-6 md:p-6 animate-pulse">
                  <div className="w-16 h-16 mb-4 rounded-full bg-[#1A2B4D]/10"></div>
                  <div className="h-6 bg-[#1A2B4D]/10 rounded mb-3"></div>
                  <div className="h-20 bg-[#1A2B4D]/5 rounded mb-6"></div>
                  <div className="h-10 w-28 bg-[#1A2B4D]/10 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
            {displayCategories.slice(0, 5).map((category, index) => (
              <CategoryCard key={index} {...category} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedCategories;
